import CalendarMarker from '@customily/customily-calendar/src/marker';
import type { MaskDto } from '@/api/typings/mask';
import type { UserFont } from '@/composables/fonts';
import { sanitizeSvgString } from '@/utils/fabricUtils';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import type { CalendarConfig, CalendarMarkerConfig, CalendarMarkerType } from './types';

export type CalendarPresetMarker = Exclude<CalendarMarkerType, 'custom'>;

export interface CalendarMarkerOption {
    value: CalendarPresetMarker;
    label: string;
}

export const CALENDAR_MARKER_OPTIONS: CalendarMarkerOption[] = [
    { value: 'heart', label: 'Heart' },
    { value: 'heart_outline', label: 'Heart outline' },
    { value: 'circle', label: 'Circle' },
    { value: 'rect', label: 'Rect' },
    { value: 'pin', label: 'Pin' },
    { value: 'pencil', label: 'Pencil' }
];

export const DEFAULT_CALENDAR_MARKER: CalendarPresetMarker = 'heart';

// Negative ids can't clash with registered masks, so a picked preset is told apart by its id
const presetId = (index: number) => -(index + 1);

// Same size as uploaded mask thumbnails; an svg with only a viewBox collapses inside the picker's <img>
const MARKER_THUMBNAIL_SIZE = 50;
const SVG_NS = 'http://www.w3.org/2000/svg';
// Same tone as the Customily shapes in the picker, the marker's own color is shown by its color field
const MARKER_THUMBNAIL_COLOR = '#0D0324';
const MARKER_THUMBNAIL_MARGIN = 0.05;

// Size is irrelevant since the viewBox is fitted to the result, it only sets the border/shape ratio
const MARKER_THUMBNAIL_CONFIG: CalendarMarkerConfig = {
    markColor: MARKER_THUMBNAIL_COLOR,
    markBorderColor: MARKER_THUMBNAIL_COLOR,
    markFill: true,
    markBorderSize: '0.6',
    dayFontSize: '7',
    customMarker: ''
};

/**
 * Thumbnail drawn by the lib itself as a filled silhouette. Each marker is placed with its own offsets
 * around the day, so the viewBox is fitted to the measured bounds.
 */
export const renderMarkerThumbnail = (type: CalendarMarkerType, customMarker = ''): string => {
    const markup = CalendarMarker.create(type, 0, 0, { ...MARKER_THUMBNAIL_CONFIG, customMarker });
    if (!markup.trim()) return '';

    // getBBox only measures attached elements
    const measurer = document.createElementNS(SVG_NS, 'svg');
    measurer.setAttribute('style', 'position:absolute;visibility:hidden;width:0;height:0');
    measurer.innerHTML = markup;
    document.body.appendChild(measurer);
    const box = measurer.getBBox();
    measurer.remove();

    // getBBox ignores stroke width, the margin keeps the border from being clipped
    const margin = Math.max(box.width, box.height) * MARKER_THUMBNAIL_MARGIN;
    const viewBox = [box.x - margin, box.y - margin, box.width + margin * 2, box.height + margin * 2].join(' ');

    return svgToDataUrl(
        `<svg xmlns="${SVG_NS}" viewBox="${viewBox}" width="${MARKER_THUMBNAIL_SIZE}" height="${MARKER_THUMBNAIL_SIZE}">${markup}</svg>`
    );
};

/** The lib's built-in markers as mask picker items; unlike uploaded svgs they honor marker color and fill. */
export const getCalendarMarkerPresets = (): MaskDto[] =>
    CALENDAR_MARKER_OPTIONS.map((option, index) => ({
        id: presetId(index),
        path: '',
        name: option.label,
        thumbnailPath: renderMarkerThumbnail(option.value)
    }));

export const findPresetMarker = (mask: MaskDto): CalendarPresetMarker | undefined =>
    CALENDAR_MARKER_OPTIONS.find((_, index) => presetId(index) === mask.id)?.value;

const CONTENT_PREFIX = 'Content/';

// The calendar lib fetches fonts by absolute URL, while user fonts are stored by their Content/ path
export const fontUrlFromPath = (fontPath: string): string =>
    `${process.env.VUE_APP_CLOUDFRONT_URL}${fontPath.replace(CONTENT_PREFIX, '')}`;

export const findFontByUrl = (fonts: UserFont[], fontUrl?: string): UserFont | undefined => {
    if (!fontUrl) return undefined;
    return fonts.find((font) => fontUrl.endsWith(font.Path.replace(CONTENT_PREFIX, '')));
};

/** Comma lists are split without trimming by the lib, so stray spaces would end up in the render. */
export const normalizeCommaList = (value: string): string =>
    value
        .split(',')
        .map((item) => item.trim())
        .join(',');

// Relative to the day font so the marker surrounds the number like the built-in ones
export const DEFAULT_CUSTOM_MARKER_SCALE = 1.4;
// The svg is stored inline in calendarConfig, which travels with every template and storefront load
export const CUSTOM_MARKER_MAX_KB = 20;

const markerSvgCache = new Map<string, Promise<string>>();

const fetchMarkerSvg = (path: string): Promise<string> => {
    let request = markerSvgCache.get(path);
    if (!request) {
        request = fetch(buildCloudFrontUrl(path)).then((response) => {
            if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
            return response.text();
        });
        // A failed request is not cached so the next attempt can retry it
        request.catch(() => markerSvgCache.delete(path));
        markerSvgCache.set(path, request);
    }
    return request;
};

/**
 * The lib injects customMarker as-is centered on the selected day, so the svg is sanitized and sized
 * to the day font here.
 */
const buildCustomMarker = (svgText: string, size: number): string => {
    const doc = new DOMParser().parseFromString(sanitizeSvgString(svgText), 'image/svg+xml');
    const svg = doc.documentElement;
    if (svg.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) {
        throw new Error('The file is not a valid SVG.');
    }

    // Content that never renders, dropped to keep the inline config small
    svg?.querySelectorAll('title, desc, metadata')?.forEach((node) => node.remove?.());
    const walker = doc.createTreeWalker(svg, NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT);
    const removable: Node[] = [];
    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeType === Node.COMMENT_NODE || !node.textContent?.trim()) removable.push(node);
    }
    removable.forEach((node) => node.parentNode?.removeChild(node));

    if (!svg.getAttribute('viewBox')) {
        const width = parseFloat(svg.getAttribute('width') || '');
        const height = parseFloat(svg.getAttribute('height') || '');
        if (width && height) svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }

    svg.setAttribute('x', String(-size / 2));
    svg.setAttribute('y', String(-size / 2));
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));

    const marker = new XMLSerializer().serializeToString(svg);
    const markerKb = new Blob([marker]).size / 1024;
    if (markerKb > CUSTOM_MARKER_MAX_KB) {
        throw new Error(`The marker is too large (${Math.ceil(markerKb)}KB, max ${CUSTOM_MARKER_MAX_KB}KB).`);
    }
    return marker;
};

export const loadCustomMarker = async (
    path: string,
    dayFontSize: string,
    scale = DEFAULT_CUSTOM_MARKER_SCALE
): Promise<Pick<CalendarConfig, 'markerType' | 'customMarker' | 'customMarkerPath' | 'customMarkerScale'>> => ({
    markerType: 'custom',
    customMarker: buildCustomMarker(await fetchMarkerSvg(path), Number(dayFontSize) * scale),
    customMarkerPath: path,
    customMarkerScale: scale
});

export const svgToDataUrl = (svg: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
